import { FC, ReactElement, useEffect } from "react";
import { Navigate } from "react-router-dom";

import { useGetUserDetailsQuery } from "@store/services/user";
import { useFlagsStatus } from "@unleash/proxy-client-react";
import { setStorageItem } from "@testavivadk/utilities";
import { useAmplitudeIdentify } from "@hooks/amplitude/useIdentifyAmplitudeUser";
import { useIdentifyAmplitudeFeatureFlags } from "@hooks/amplitude/useIdentifyAmplitudeFeatureFlags";
import { useCartSanityCheck } from "@hooks/useCartSanityCheck";
import i18n from "../../i18n";

import MuiLoadingContainer from "@components/MuiLoadingContainer";
import { Alert } from "@mui/material";

import { applicationSpaRoutes } from "@shared/constants/routes";
import { loginRedirectKey } from "@constants";
import { isStartUnleashClient } from "@providers/UnleashProvider/UnleashProvider.utilities";

const FallBack: FC = () => <Alert severity="error">{i18n.t("global.errors.genericError")}</Alert>;

const ProtectedRoute: FC<{ children: ReactElement }> = ({ children }) => {
  const { data: userDetails, isLoading: isUserDetailsLoading } = useGetUserDetailsQuery();

  const { identifyUser: identifyAmplitudeUser } = useAmplitudeIdentify();
  useIdentifyAmplitudeFeatureFlags();

  const flagsStatus = useFlagsStatus();
  const flagsReady = flagsStatus?.flagsReady ?? true;
  const isFlagsReady = isStartUnleashClient ? flagsReady : true;

  useCartSanityCheck();

  useEffect(() => {
    if (isUserDetailsLoading) {
      return;
    }

    if (userDetails?.id != null) {
      identifyAmplitudeUser(userDetails);
    }
  }, [userDetails, isUserDetailsLoading, identifyAmplitudeUser]);

  if (isUserDetailsLoading || !isFlagsReady) {
    return <MuiLoadingContainer />;
  }

  const loginRedirect = `${window.location.pathname}${window.location.search}`;
  if (userDetails?.authenticated == null || !userDetails?.authenticated) {
    if (!["/", "/login"].includes(loginRedirect)) {
      setStorageItem(loginRedirectKey, loginRedirect);
    }

    return <Navigate to={applicationSpaRoutes.login} />;
  }

  const shouldRedirect = userDetails?.authenticated && (loginRedirect === "/" || loginRedirect.includes("/?_gl="));
  if (shouldRedirect) {
    return <Navigate to={applicationSpaRoutes.myOverview} />;
  }

  return children;
};

const LogoutOnlyRoute: FC<{ children: ReactElement }> = ({ children }) => {
  const { data: userDetails, isLoading: isUserDetailsLoading } = useGetUserDetailsQuery();

  if (isUserDetailsLoading) {
    return <MuiLoadingContainer />;
  }

  if (userDetails?.authenticated) {
    return <Navigate to={applicationSpaRoutes.myOverview} />;
  }

  return children;
};

export { FallBack, ProtectedRoute, LogoutOnlyRoute };
